import React, { useEffect, useState } from 'react';
import { Star, Users, Award, Heart, Brain, Target, Phone, CheckCircle, Play, Facebook, Instagram, Youtube } from 'lucide-react';
import { fetchInstructors } from './instructorPageApi';
import { Link, useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { getPillarMeta, getSpecialityLabel } from '../../../utils/breadcrumb';
import SEO from '../../SEO';

// Updated interface to match your API data structure
interface ApiInstructor {
  firstname: string;
  lastname: string;
  useremail: string;
  mentorId: string;
  profileIcon?: string;
  profileQuote?: string;
  bio?: string;
  languages?: string[];
  location?: string;
  phone?: string;
  createAt: string;
  updateAt: string;
  emailVerified: boolean;
  agreeToTerms: boolean;
  subscribeNewsletter: boolean;
  certification?: string[];
  experience?: string;
  socialLinks: {
    demoVideo?: string;
    facebook?: string;
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
    youtube?: string;
  };
  Skills: string[];
  slug: string;
  latestClass?: {
    _id: string;
    classTitle: string;
    pricingType?: string;
  }[];
}

interface Instructor {
  mentorId: string;
  name: string;
  specialty: string;
  experience: string;
  rating: number;
  reviews: number;
  price: string;
  languages: string[];
  image: string;
  profileQuote?: string;
  certification: string[];
  description: string;
  availability: string;
  createdAt: string;
  demoVideoUrl?: string;
  socialLinks?: {
    demoVideo?: string;
    facebook?: string;
    instagram?: string;
    telegram?: string;
    whatsapp?: string;
    youtube?: string;
  };
  Skills: string[];
  slug: string;
  latestClassTitle?: string; // New field
  hasDemo?: boolean; // New field
}

interface InstructorProps {
  data: ApiInstructor[];
}

// Function to transform API data to display format
const transformApiToInstructor = (apiData: ApiInstructor): Instructor => {
  let quote = apiData.profileQuote;
  if (quote) {
    // Remove inline styles to allow tailwind to take over
    quote = quote.replace(/ style="[^"]*"/g, '');
    // Make paragraphs flow inline with breaks
    quote = quote.replace(/<p>/g, '').replace(/<\/p>/g, '');
  }


  let skills = apiData.Skills || [];
  if (skills.length === 1 && typeof skills[0] === 'string' && skills[0].includes(',')) {
    skills = skills[0].split(/,\s*/);
  }

  let priceValue = "Paid";
  let latestClassTitle: string | undefined;
  let hasDemo: boolean | undefined = false;

  if (apiData.latestClass && apiData.latestClass.length > 0) {
    const firstClass = apiData.latestClass[0];
    latestClassTitle = firstClass.classTitle;
    if (firstClass.pricingType && (firstClass.pricingType.toLowerCase() === 'free' || firstClass.pricingType.toLowerCase() === 'demo')) {
      priceValue = "Free Demo";
      hasDemo = true;
    }
  }

  return {
    mentorId: apiData.mentorId,
    name: `${apiData.firstname} ${apiData.lastname}`,
    specialty: apiData.bio || 'Wellness Coach',
    experience: apiData.experience || 'New',
    rating: 4.5 + Math.random() * 0.5,
    reviews: Math.floor(Math.random() * 50) + 10,
    price: priceValue, // Use the calculated price
    languages: apiData.languages || ['English'],
    image: apiData.profileIcon || '',
    profileQuote: quote,
    certification: apiData.certification || ['Certified Wellness Coach', 'Holistic Wellness'],
    description: apiData.bio || `Experienced wellness professional dedicated to helping you achieve balance and better health.`,
    availability: 'Available',
    createdAt: apiData.createAt,
    demoVideoUrl: apiData.socialLinks?.demoVideo,
    socialLinks: apiData.socialLinks,
    Skills: skills,
    slug: apiData.slug || '',
    latestClassTitle: latestClassTitle,
    hasDemo: hasDemo,
  };
};

const dedupeInstructors = (items: Instructor[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.slug || item.mentorId || item.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeFilterValue = (value: unknown) =>
  String(value || '').trim().toLocaleLowerCase();

export default function YogaInstructorPage() {
  const navigate = useNavigate();

  const categories = [
    {
      id: 'physical',
      name: 'Physical Fitness',
      icon: <Heart className="w-8 h-8" />,
      description: 'Build strength, mobility, and endurance with expert guidance',
      features: ['Strength Building', 'Flexibility', 'Stretching', 'Core Work']
    },
    {
      id: 'mental',
      name: 'Mental Fitness',
      icon: <Brain className="w-8 h-8" />,
      description: 'Enhance clarity and emotional balance through mindful practices',
      features: ['Meditation', 'Breathing', 'Mindfulness', 'Stress Relief']
    },
    {
      id: 'holistic',
      name: 'Holistic Goals',
      icon: <Target className="w-8 h-8" />,
      description: 'A complete wellness approach for mind, body, and spirit alignment',
      features: ['Life Balance', 'Spiritual Growth', 'Detoxification', 'Energy Healing']
    }
  ];

  // State for all instructors fetched from API
  const [instructors, setInstructors] = useState<Instructor[]>([]);

  // State for filtered instructors based on search, price, skills
  const [filteredInstructors, setFilteredInstructors] = useState<Instructor[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
  const [allSkills, setAllSkills] = useState<string[]>([]);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);

  const testimonials = [
    {
      name: 'Sneha Devi',
      text: 'The sessions helped me stay consistent and feel stronger every week. I really like the personalized guidance.',
      rating: 5
    },
    {
      name: 'Rahul Jha',
      text: 'My mind feels calmer and my routine is much better now. The guidance has made a real difference.',
      rating: 5
    },
    {
      name: 'Tina Mehta, IT',
      text: 'The mind-body programs, bro I selected it at the end are my life goal by to much love everything for this.',
      rating: 5
    },
    {
      name: 'Amit Verma, CA',
      text: 'My sessions helped me regain mind peace and inner calm. Wonderful teacher and great experience.',
      rating: 5
    }
  ];

  const benefits = [
    'Tailored Wellness Plans: Customized sessions based on your fitness level and goals',
    'Holistic Wellness: Integrating physical poses, breathing, and meditation',
    'Expert Instructors: Certified professionals with years of teaching experience',
    'Flexible Sessions: Choose your convenient time slots and session duration'
  ];

  const expectations = [
    'Physical Benefits: Improve strength, flexibility, and balance',
    'Mental Clarity: Enhanced focus and reduced stress levels',
    'Spiritual Growth: Deeper connection with inner self through mindfulness',
    'Stress Relief: Learn techniques to manage daily stress and anxiety'
  ];

  const suitability = [
    'Can\'t find the right wellness style?',
    'New to guided wellness sessions?',
    'Looking for personalized attention?',
    'Want to practice from home?',
    'Seeking stress management?',
    'Need flexible scheduling?'
  ];

  // Function to handle "Try Free Session" button clicks
  const handleTryFreeSession = () => {
    // Navigate to classes page with free filter parameter
    navigate('/classes?filter=Demo');
  };

  useEffect(() => {
    const fetchAndSetInstructors = async () => {
      try {
        setLoading(true);
        const res = (await fetchInstructors()) as InstructorProps;
        if (res && res.data && Array.isArray(res.data)) {
          const transformedInstructors = dedupeInstructors(res.data.map(transformApiToInstructor));
          setInstructors(transformedInstructors);

          // Extract all unique skills
          const skills = new Set<string>();
          transformedInstructors.forEach(instructor => {
            instructor.Skills.forEach(skill => skills.add(skill));
          });
          setAllSkills(Array.from(skills));

          // Extract all unique languages
          const languages = new Set<string>();
          transformedInstructors.forEach(instructor => {
            instructor.languages.forEach(lang => languages.add(lang));
          });
          setAllLanguages(Array.from(languages));

        } else {
          console.error('Invalid response structure:', res);
          setError('Invalid data structure received from API');
          setInstructors([]);
        }
      } catch (err) {
        console.error('Error fetching instructors:', err);
        setError('Failed to fetch instructors');
        setInstructors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAndSetInstructors();
  }, []);

  // Apply filters and update displayed instructors
  useEffect(() => {
    let filtered = instructors;

    // Filter by search term
    if (searchTerm.trim()) {
      const query = normalizeFilterValue(searchTerm);
      filtered = filtered.filter(instructor =>
        [
          instructor.name,
          instructor.specialty,
          instructor.description,
          ...instructor.languages,
          ...instructor.Skills,
        ].some(value => normalizeFilterValue(value).includes(query))
      );
    }

    // Filter by language
    if (languageFilter !== 'all') {
      const selectedLanguage = normalizeFilterValue(languageFilter);
      filtered = filtered.filter(instructor =>
        instructor.languages.some(language =>
          normalizeFilterValue(language) === selectedLanguage
        )
      );
    }

    // Filter by skills
    if (skillsFilter.length > 0) {
      filtered = filtered.filter(instructor =>
        skillsFilter.every(selectedSkill =>
          instructor.Skills.some(skill =>
            normalizeFilterValue(skill) === normalizeFilterValue(selectedSkill)
          )
        )
      );
    }

    setFilteredInstructors(filtered);
  }, [instructors, searchTerm, languageFilter, skillsFilter]);

  const handleVideoClick = (url: string) => {
    window.open(url, "_blank");
  };

  const openInstructorProfile = (slug: string) => {
    const matched = instructors.find((item) => item.slug === slug || item.mentorId === slug);
    const matchedInstructor = matched as Instructor | undefined;
    const specialityLabel = getSpecialityLabel(matchedInstructor?.specialty || matchedInstructor?.Skills?.[0] || 'Wellness Coach');
    const pillarMeta = getPillarMeta(specialityLabel);

    navigate(`/public/instructor-profile/${slug}`, {
      state: {
        sourcePillar: pillarMeta,
        sourceSpeciality: specialityLabel,
        sourceName: matched?.name,
      },
    });
  };

  const handleFetchMentorDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = (await fetchInstructors()) as InstructorProps;

      if (res && res.data && Array.isArray(res.data)) {
        const transformedInstructors = dedupeInstructors(res.data.map(transformApiToInstructor));
        setInstructors(transformedInstructors);
      } else {
        setError('Invalid data structure received from API');
        setInstructors([]);
      }
    } catch (err) {
      setError('Failed to fetch instructors');
      setInstructors([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true, // Animation happens only once
      offset: 50, // Smaller offset for earlier trigger
      delay: 0,
      easing: 'ease-out',
      disable: false, // Set to 'mobile' to disable on mobile, or true to disable completely
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F0E3] text-[#15302B]">
      <SEO
        title="Certified Yoga Instructors & Practitioners | MetaFit Wellness"
        description="Book personalized 1-on-1 and group yoga sessions with certified yoga instructors across India. Practice Hatha, Vinyasa, Yin, and Ashtanga yoga online."
        keywords="yoga instructors India, certified yoga teachers, online yoga classes, private yoga sessions, Hatha yoga, Vinyasa yoga, Pranayama"
        url="https://www.metafitwellness.com/yoga-instructor"
        canonical="https://www.metafitwellness.com/yoga-instructor"
        image="https://www.metafitwellness.com/metafit.webp"
      />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden bg-gradient-to-b from-[#F7F1E5] via-[#F5F0E3] to-[#EEF1EA]"
        style={{ padding: '16px 0' }}
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#1C8F86] via-[#E6A93F] to-[#2E67F8]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl mx-auto">
            <p className="mb-1 text-[9px] font-semibold tracking-[0.2em] uppercase text-[#1C8F86]">
              Meet your instructors
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-[#15302B] leading-tight tracking-[-0.02em] mb-1.5">
              Experience Personalized <span className="whitespace-nowrap">Wellness Coaching</span>
            </h1>
            <p className="text-xs sm:text-sm font-medium text-[#4D625D] leading-5 max-w-3xl mx-auto">
              Personalized wellness sessions for balance, strength and peace of mind—guided by
              trusted instructors from the comfort of your home.
            </p>
          </div>
          {/* <div className="text-center">
            <button
              onClick={handleTryFreeSession}
              className="bg-yellow-300 hover:bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Find Your Classes
            </button>
          </div> */}
        </div>
      </section>


      {/* Instructors Grid */}
      <section
        id="instructors-section"
        className="bg-[#F5F0E3]"
        style={{ padding: '12px 0' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Enhanced Filter Section */}
          <div className="mb-5 rounded-2xl border border-[#E0D4B8] bg-[#F8F2E7] p-4 shadow-[0_14px_32px_-26px_rgba(21,48,43,0.26)]">
            <div className="flex items-center justify-between gap-3 mb-2.5">
              <h3 className="text-sm font-semibold text-[#15302B] flex items-center">
                <svg className="w-4 h-4 mr-1.5 text-[#1C8F86]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                </svg>
                Find Instructors
              </h3>
              <div className="flex items-center gap-3">
                <div className="px-3 py-1 rounded-full border border-[#BFEBDD] bg-[#DDF8F2]">
                  <p className="text-xs text-[#166F68]">
                    <span className="font-semibold">{filteredInstructors.length}</span> {filteredInstructors.length === 1 ? 'instructor' : 'instructors'}
                  </p>
                </div>
                {(searchTerm || languageFilter !== 'all' || skillsFilter.length > 0) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setLanguageFilter('all');
                      setSkillsFilter([]);
                    }} className="text-xs text-[#1C8F86] hover:text-[#166F68] font-semibold flex items-center transition-colors">
                    <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {/* Search Input */}
              <div className="group">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400 group-focus-within:text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full rounded-2xl border border-[#D9CFB7] bg-[#FFFDF7] py-3 pl-9 pr-3 text-sm text-[#15302B] shadow-sm outline-none focus:border-[#1C8F86] focus:ring-4 focus:ring-[#BFEBDD]"
                    placeholder="Search name..."
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute inset-y-0 right-0 pr-2 flex items-center">
                      <svg className="w-3 h-3 text-[#8A948C] hover:text-[#15302B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Language Filter */}
              <div className="group">
                <div className="relative">
                  <select
                    id="language"
                    value={languageFilter}
                    onChange={(e) => setLanguageFilter(e.target.value)}
                    className="block w-full appearance-none cursor-pointer rounded-2xl border border-[#D9CFB7] bg-[#FFFDF7] px-3 py-3 text-sm text-[#15302B] shadow-sm outline-none focus:border-[#1C8F86] focus:ring-4 focus:ring-[#BFEBDD]">
                    <option value="all">All Languages</option>
                    {allLanguages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-[#8A948C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {languageFilter !== 'all' && (
                    <div className="absolute top-0.5 right-6 w-1.5 h-1.5 rounded-full bg-[#1C8F86]"></div>
                  )}
                </div>
              </div>

              {/* Skills Filter */}
              <div className="group">
                <div className="relative">
                  <select
                    id="skills"
                    value={skillsFilter.length === 0 ? 'all' : skillsFilter[0]}
                    onChange={(e) => {
                      if (e.target.value === 'all') {
                        setSkillsFilter([]);
                      } else {
                        setSkillsFilter([e.target.value]);
                      }
                    }}
                    className="block w-full appearance-none cursor-pointer rounded-2xl border border-[#D9CFB7] bg-[#FFFDF7] px-3 py-3 text-sm text-[#15302B] shadow-sm outline-none focus:border-[#1C8F86] focus:ring-4 focus:ring-[#BFEBDD]"
                  >
                    <option value="all">All Skills</option>
                    {allSkills.map(skill => (
                      <option key={skill} value={skill}>{skill}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-[#8A948C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {skillsFilter.length > 0 && (
                    <div className="absolute top-0.5 right-6 w-1.5 h-1.5 rounded-full bg-[#1C8F86]"></div>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchTerm || languageFilter !== 'all' || skillsFilter.length > 0) && (
              <div className="mt-4 pt-4 border-t border-[#E0D4B8]">
                <div className="flex flex-wrap items-center gap-1.5">
                  {searchTerm && (
                    <span className="inline-flex items-center rounded-full border border-[#BFEBDD] bg-[#DDF8F2] px-2 py-0.5 text-xs font-medium text-[#166F68]">
                      {searchTerm}
                      <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-[#15302B]">
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  )}

                  {languageFilter !== 'all' && (
                    <span className="inline-flex items-center rounded-full border border-[#CFE0FF] bg-[#EDF4FF] px-2 py-0.5 text-xs font-medium text-[#2E67F8]">
                      {languageFilter}
                      <button onClick={() => setLanguageFilter('all')} className="ml-1 hover:text-[#2E67F8]">
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  )}

                  {skillsFilter.map((skill, index) => (
                    <span key={index} className="inline-flex items-center rounded-full border border-[#E0D4B8] bg-[#F8F2E7] px-2 py-0.5 text-xs font-medium text-[#15302B]">
                      {skill}
                      <button onClick={() => setSkillsFilter(skillsFilter.filter(s => s !== skill))} className="ml-1 hover:text-[#1C8F86]">
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
              <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-[#1C8F86]"></div>
              <p className="mt-2 text-[#4D625D]">Loading instructors...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <p className="mb-4 text-red-600">{error}</p>
              <button
                onClick={handleFetchMentorDetails}
                className="rounded-full bg-[#1C8F86] px-4 py-2 text-white transition-colors hover:bg-[#166F68]"
              >
                Try Again
              </button>
            </div>
          )}
          {/* Instructors Grid - Show displayed instructors */}
          {!loading && !error && filteredInstructors && filteredInstructors.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInstructors.map((instructor) => (
                <div
                  data-aos="fade-up"
                  key={instructor.mentorId}
                  role="button"
                  tabIndex={0}
                  onClick={() => openInstructorProfile(instructor.slug || instructor.mentorId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openInstructorProfile(instructor.slug || instructor.mentorId);
                    }
                  }}
                  className="group relative flex cursor-pointer flex-col overflow-hidden rounded-[2rem] border border-[#E0D4B8] bg-[#F8F2E7] shadow-[0_18px_40px_-28px_rgba(21,48,43,0.3)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_46px_-24px_rgba(21,48,43,0.36)] focus:outline-none focus:ring-2 focus:ring-[#1C8F86] focus:ring-offset-2 focus:ring-offset-[#F5F0E3]"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D3F7F1] via-[#EEF1EA] to-[#EDE6FD] opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>

                  {/* Main Content Container */}
                  <div className="relative p-4 lg:p-6 flex-grow flex flex-col gap-3">

                    {/* Top Section */}
                    <div className="flex items-start gap-4">
                      {/* Profile Image Container */}
                      <div className="flex-shrink-0 w-24 h-24 rounded-2xl overflow-hidden border-4 border-[#E0D4B8] shadow-xl transform transition-transform duration-300 group-hover:scale-105">
                        {instructor.image ? (
                          <img src={instructor.image} alt={instructor.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-[#1C8F86] flex items-center justify-center">
                            <span className="text-white text-2xl font-bold">{instructor.name.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>

                      {/* Header */}
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-[#15302B] leading-tight">{instructor.name}</h3>
                        </div>
                        <p className="text-[#4D625D] text-sm">{instructor.experience}</p>
                        {/* Rating */}
                        <div className="flex items-center gap-1 text-[#E6A93F] mt-1">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm font-semibold text-[#15302B]">{Number.isInteger(instructor.rating) ? instructor.rating : instructor.rating.toFixed(1)}/5</span>
                        </div>
                      </div>
                    </div>

                    {/* Social and Video */}
                    <div className="flex items-center gap-4">
                      {instructor.demoVideoUrl && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleVideoClick(instructor.demoVideoUrl!);
                          }}
                          className="flex items-center gap-2 text-[#1C8F86] hover:text-[#166F68] transition-colors text-sm font-semibold"
                        >
                          <Play className="w-5 h-5 fill-current" />
                          <span>Watch Intro</span>
                        </button>
                      )}
                      <div className="flex items-center gap-3 ml-auto">
                        {instructor.socialLinks?.facebook && (
                          <a
                            href={instructor.socialLinks.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="text-[#8A948C] hover:text-[#2E67F8] transition-colors"
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}
                        {instructor.socialLinks?.instagram && (
                          <a
                            href={instructor.socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="text-[#8A948C] hover:text-[#B9576B] transition-colors"
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}
                        {instructor.socialLinks?.youtube && (
                          <a
                            href={instructor.socialLinks.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="text-[#8A948C] hover:text-[#C98726] transition-colors"
                          >
                            <Youtube className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Profile Quote */}
                    {/* ✅ CRITICAL FIX: Render as plain text instead of HTML to prevent XSS */}
                       {instructor.profileQuote && (
                      <blockquote
                        className="my-1 border-l-2 border-[#1C8F86]/70 pl-3 text-sm italic text-[#4D625D]"
                        dangerouslySetInnerHTML={{ __html: instructor.profileQuote }}
                      />
                    )}

                    {/* Description */}
                    <p className="flex-grow line-clamp-2 text-sm leading-relaxed text-[#4D625D]">{instructor.description}</p>

                    {/* Expertise */}
                    <div className="text-sm text-[#15302B]">
                      <span className="font-bold">Expertise: </span>
                      <span className="line-clamp-2 text-[#4D625D]">
                        {instructor.Skills.slice(0, 8).join(', ')}
                        {instructor.Skills.length > 6 && '...'}
                      </span>
                    </div>
                    {/* Latest Class Name */}
                    {instructor.latestClassTitle && (
                      <div className="mt-1 text-sm text-[#15302B]">
                        <span className="font-bold">Latest Class: </span>
                        <span className="text-[#4D625D]">{instructor.latestClassTitle}</span>
                      </div>
                    )}

                    {/* Footer (Price & CTA) */}
                    <div className="mt-auto flex items-center justify-between border-t border-[#E0D4B8] pt-3">
                      <div className="flex items-baseline">
                        <span className="text-xl font-semibold text-[#1C8F86]">
                          {instructor.price !== 'Free' ? `${instructor.price}` : instructor.price}
                        </span>
                        {instructor.price !== 'Free'}
                      </div>
                      <Link
                        to={`/public/instructor-profile/${instructor.slug || instructor.mentorId}`}
                        state={{
                          sourcePillar: getPillarMeta(instructor.specialty || instructor.Skills?.[0] || 'Move'),
                          sourceSpeciality: getSpecialityLabel(instructor.specialty || instructor.Skills?.[0] || 'Wellness Coach'),
                          sourceName: instructor.name,
                        }}
                        onClick={(event) => event.stopPropagation()}
                        className="rounded-full bg-[#1C8F86] px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[#166F68]"
                      >
                        View Profile
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* No Data State */}
          {!loading && !error && filteredInstructors && filteredInstructors.length === 0 && (
            <div className="text-center py-8">
              <p className="text-[#4D625D]">No instructors match the current filters.</p>
              <button
                onClick={handleFetchMentorDetails}
                className="mt-4 rounded-full bg-[#1C8F86] px-4 py-2 text-white transition-colors hover:bg-[#166F68]"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-[#F5F0E3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="rounded-[1.8rem] border border-[#E0D4B8] bg-[#F8F2E7] p-6 shadow-[0_16px_36px_-28px_rgba(21,48,43,0.24)]">
              <div className="flex items-center mb-4">
                <Award className="w-6 h-6 text-[#1C8F86] mr-2" />
                <h3 className="text-lg font-semibold text-[#15302B]">Why Choose Us</h3>
              </div>
              <ul className="space-y-2">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start text-sm text-[#4D625D]">
                    <CheckCircle className="w-4 h-4 text-[#1C8F86] mr-2 mt-0.5 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-[1.8rem] border border-[#E0D4B8] bg-[#F8F2E7] p-6 shadow-[0_16px_36px_-28px_rgba(21,48,43,0.24)]">
              <div className="flex items-center mb-4">
                <Target className="w-6 h-6 text-[#E6A93F] mr-2" />
                <h3 className="text-lg font-semibold text-[#15302B]">What You Can Expect</h3>
              </div>
              <ul className="space-y-2">
                {expectations.map((expectation, index) => (
                  <li key={index} className="flex items-start text-sm text-[#4D625D]">
                    <CheckCircle className="w-4 h-4 text-[#E6A93F] mr-2 mt-0.5 flex-shrink-0" />
                    {expectation}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-[1.8rem] border border-[#E0D4B8] bg-[#F8F2E7] p-6 shadow-[0_16px_36px_-28px_rgba(21,48,43,0.24)]">
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-[#2E67F8] mr-2" />
                <h3 className="text-lg font-semibold text-[#15302B]">Is This For You?</h3>
              </div>
              <ul className="space-y-2">
                {suitability.map((point, index) => (
                  <li key={index} className="flex items-start text-sm text-[#4D625D]">
                    <CheckCircle className="w-4 h-4 text-[#2E67F8] mr-2 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>



      {/* Coaching Categories */}
      <section className="py-16 bg-gradient-to-b from-[#15302B] to-[#2C4A3B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              Personalized 1-on-1 Wellness Coaching – Live on Zoom
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {categories.map((category) => (
              <div key={category.id} className="rounded-[1.6rem] border border-white/10 bg-white/6 p-6 text-center shadow-[0_18px_40px_-28px_rgba(0,0,0,0.38)] backdrop-blur-sm hover:border-[#D3F7F1]/50 transition-colors">
                <div className="flex items-center justify-center mb-4">
                  <div className="text-[#D3F7F1]">
                    {category.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white ml-3">{category.name}</h3>
                </div>
                <p className="text-white/75 mb-4 text-sm">{category.description}</p>
                <ul className="space-y-1">
                  {category.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-white/65">
                      <CheckCircle className="w-4 h-4 text-[#D3F7F1] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Metafit Advantage Section */}
      <section className="py-16 bg-[#F5F0E3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#15302B] mb-4">
              The Metafit Advantage
            </h2>
            <p className="text-[#4D625D] text-lg">
              Discover the benefits of personalized wellness with our expert instructors.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-[#1C8F86] mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#15302B] mb-2">Personalized Plans</h3>
                <p className="text-[#4D625D]">Customized wellness plans tailored to your individual needs and goals.</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-[#1C8F86] mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#15302B] mb-2">Expert Instructors</h3>
                <p className="text-[#4D625D]">Learn from certified and experienced instructors who care about your progress.</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-[#1C8F86] mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#15302B] mb-2">Flexible Scheduling</h3>
                <p className="text-[#4D625D]">Book sessions at your own convenience with our flexible scheduling options.</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-[#1C8F86] mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#15302B] mb-2">Holistic Approach</h3>
                <p className="text-[#4D625D]">We focus on the mind, body, and spirit to help you achieve overall wellness.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[#F5F0E3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-semibold text-[#15302B] mb-4">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="rounded-[1.4rem] border border-[#E0D4B8] bg-[#F8F2E7] p-6 shadow-[0_16px_36px_-28px_rgba(21,48,43,0.24)]">
                <div className="flex items-center mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current text-[#E6A93F]" />
                  ))}
                </div>
                <p className="mb-3 text-sm text-[#4D625D]">"{testimonial.text}"</p>
                <p className="font-medium text-[#15302B]">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#166F68] via-[#1C8F86] to-[#2E67F8] py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-3xl font-semibold text-white">Ready to Begin Your Wellness Journey?</h2>
          <p className="mb-8 text-xl text-white/80">
            Start with a free session and discover the right specialist for your needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleTryFreeSession}
              className="rounded-full bg-[#F8F2E7] px-8 py-3 text-[#15302B] font-semibold transition-transform hover:-translate-y-0.5 hover:bg-white"
            >
              Try Free Session
            </button>
            <Link to={"tel:+919982498555"} className="flex items-center justify-center rounded-full border border-white/40 px-8 py-3 font-medium text-white transition-colors hover:bg-white hover:text-[#166F68]">
              <Phone className="w-5 h-5 mr-2" />
              Call for Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
